import type {ImageField, KeyTextField, LinkField, RichTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {CustomerStoryDocumentData, TestimonialsSectionSliceV2PrimaryTestimonialsItem} from 'prismicio-types'

const QUOTE_INDEX_BY_OPTION: Record<string, number> = {
  '1st quote': 0,
  '2nd quote': 1,
  '3rd quote': 2,
  '4th quote': 3,
  '5th quote': 4,
}

export type ResolvedTestimonial = Pick<
  TestimonialsSectionSliceV2PrimaryTestimonialsItem,
  'tab_title' | 'image' | 'quote' | 'author_name'
> & {
  author_role: KeyTextField
  author_company: KeyTextField
  company_logo: ImageField
  link_url: LinkField
  button_label: KeyTextField
}

const DEFAULT_BUTTON_LABEL_BY_LANGUAGE: Record<string, string> = {
  en: 'Read full story',
  de: 'Lies die ganze Geschichte',
  nl: 'Lees hoe ze Moss gebruiken',
}

const getDefaultButtonLabel = (locale: string | undefined): string =>
  DEFAULT_BUTTON_LABEL_BY_LANGUAGE[locale?.split('-')[0] ?? ''] ?? DEFAULT_BUTTON_LABEL_BY_LANGUAGE.en

const toRichText = (text: string): RichTextField => [{type: 'paragraph', text, spans: []}]

const pickQuote = (data: CustomerStoryDocumentData, quoteNumber: string | null) => {
  const quotes = data.quotes.filter((quote) => isFilled.keyText(quote.quote))
  const index = QUOTE_INDEX_BY_OPTION[quoteNumber ?? ''] ?? 0

  return quotes[index] ?? quotes[0]
}

const beforeComma = (text: KeyTextField | undefined): KeyTextField => text?.split(',')[0].trim() ?? null

const textOr = (override: KeyTextField, fallback: KeyTextField): KeyTextField =>
  isFilled.keyText(override) ? override : fallback

const imageOr = (override: ImageField, fallback: ImageField | undefined): ImageField =>
  isFilled.image(override) ? override : (fallback ?? override)

export const resolveTestimonialFromStory = (
  testimonial: TestimonialsSectionSliceV2PrimaryTestimonialsItem,
  buttonText: KeyTextField,
  locale: string | undefined,
): ResolvedTestimonial => {
  const story = isFilled.contentRelationship(testimonial.customer_story_document)
    ? testimonial.customer_story_document
    : undefined

  const data = story?.data as CustomerStoryDocumentData | undefined
  const quote = data ? pickQuote(data, testimonial.quote_number) : undefined
  const storyQuote = quote?.quote ? toRichText(quote.quote) : undefined

  return {
    tab_title: testimonial.tab_title,
    quote: isFilled.richText(testimonial.quote) ? testimonial.quote : (storyQuote ?? testimonial.quote),
    author_name: textOr(testimonial.author_name, beforeComma(quote?.author_name)),
    author_role: beforeComma(quote?.author_role),
    author_company: data?.company_name ?? null,
    company_logo: data?.company_logo ?? {},
    image: imageOr(testimonial.image, data?.image),
    link_url: story ? {...story, variant: undefined} : {link_type: 'Any'},
    button_label: story ? textOr(buttonText, getDefaultButtonLabel(locale)) : null,
  }
}
